import React, { useState } from "react";
import { Menu } from "@headlessui/react";
import toast from "react-hot-toast";
import { HiDotsVertical } from "react-icons/hi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import Comments from "@/components/Comments";
import Drawer from "@/components/Drawer";
import SelectUser from "@/components/selectUser";
import TabItem from "@/components/TabItem";

import InlineText from "./inlineText";
import {
  BLOCKERS,
  EXIT_STAGES,
  SALES_STAGES,
  addDaysInput,
  blockerColor,
  dealAmount,
  formatCurrency,
  formatDate,
  isOverdue,
  stageColor,
  stageLabel,
  toInputDate,
  urgencySplit,
} from "./constants";

export default function DealDrawer({ deal, budgets = [], onClose, onUpdate }) {
  return (
    <Drawer isOpen={!!deal} onClose={onClose} className="w-full md:w-[1200px]">
      {deal && <DealPanel key={deal._id} deal={deal} budgets={budgets} onClose={onClose} onUpdate={onUpdate} />}
    </Drawer>
  );
}

// Keyed on the deal, so opening another deal starts back on the Description tab.
const DealPanel = ({ deal, budgets, onClose, onUpdate }) => {
  const user = useSelector((state) => state.Auth.user);
  const [tab, setTab] = useState("description");
  const { amount, weighted, estimated } = dealAmount(deal, budgets);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3 pr-12">
          <Link to={`/project/${deal._id}`} title="Open the project" className="text-xl font-bold text-gray-900 hover:text-blue-600 truncate">
            {deal.name}
          </Link>
          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase border ${stageColor(deal.sales_stage)}`}>
            {stageLabel(deal.sales_stage)}
          </span>
          <div className="ml-auto shrink-0 flex items-center gap-1">
            <DealMenu deal={deal} />
          </div>
        </div>
      </div>

      <nav className="flex border-b border-gray-200">
        <TabItem title="Description" active={tab === "description"} onClick={() => setTab("description")} />
        <TabItem title="Company" active={tab === "company"} onClick={() => setTab("company")} />
        <TabItem title="Contacts" active={tab === "contacts"} onClick={() => setTab("contacts")} />
        <TabItem title="Details" active={tab === "details"} onClick={() => setTab("details")} />
        <TabItem title="Action" active={tab === "action"} onClick={() => setTab("action")} />
        {budgets.length > 0 && <TabItem title="Budgets" active={tab === "budgets"} onClick={() => setTab("budgets")} />}
        <TabItem title="Context" active={tab === "context"} onClick={() => setTab("context")} />
      </nav>

      {tab === "description" && (
        <InlineText
          value={deal.description}
          placeholder="What is this deal about?"
          onSave={(description) => onUpdate(deal._id, { description })}
          isTextArea
          rows={12}
          className="text-base leading-relaxed"
        />
      )}

      {tab === "company" && <CompanyTab deal={deal} onUpdate={onUpdate} />}

      {tab === "contacts" && <ContactsTab deal={deal} onUpdate={onUpdate} />}

      {tab === "details" && (
        <>
          <Section title="Pipeline">
            <Field label="Stage">
              <select className="input w-full" value={deal.sales_stage || ""} onChange={(e) => onUpdate(deal._id, { sales_stage: e.target.value })}>
                <option value="">No stage</option>
                {SALES_STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Target signature date">
              <input
                type="date"
                className="input w-full"
                value={toInputDate(deal.target_signature_at)}
                onChange={(e) => onUpdate(deal._id, { target_signature_at: e.target.value || null })}
              />
            </Field>
            <Field label="Amount">
              <p className="text-sm text-gray-900 font-semibold">
                {formatCurrency(amount)}
                {estimated ? <span className="ml-2 text-xs font-normal text-gray-400">estimate, no budget yet</span> : null}
              </p>
              {!estimated && <p className="text-xs text-gray-500">weighted {formatCurrency(Math.round(weighted))}</p>}
            </Field>
            <Field label="Last meaningful contact">
              <input
                type="date"
                className="input w-full"
                value={toInputDate(deal.last_contact_at)}
                onChange={(e) => onUpdate(deal._id, { last_contact_at: e.target.value || null })}
              />
            </Field>
          </Section>

          <Section title="Owners">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Sales follow-up owner">
                <SelectUser
                  placeholder="Unassigned"
                  value={{ _id: deal.sales_owner_id, name: deal.sales_owner_name, avatar: deal.sales_owner_avatar }}
                  onChange={(u) => onUpdate(deal._id, { sales_owner_id: u?._id || "", sales_owner_name: u?.name || "", sales_owner_avatar: u?.avatar || "" })}
                />
              </Field>
              <Field label="Closing / proposal owner">
                <SelectUser
                  placeholder="Unassigned"
                  value={{ _id: deal.closing_owner_id, name: deal.closing_owner_name, avatar: deal.closing_owner_avatar }}
                  onChange={(u) => onUpdate(deal._id, { closing_owner_id: u?._id || "", closing_owner_name: u?.name || "", closing_owner_avatar: u?.avatar || "" })}
                />
              </Field>
              <Field label="Source / who brought the deal in">
                <SelectUser
                  placeholder="Unknown"
                  value={{ _id: deal.sales_business_provider_id, name: deal.sales_business_provider_name, avatar: deal.sales_business_provider_avatar }}
                  onChange={(u) =>
                    onUpdate(deal._id, {
                      sales_business_provider_id: u?._id || "",
                      sales_business_provider_name: u?.name || "",
                      sales_business_provider_avatar: u?.avatar || "",
                      sales_business_provider_email: u?.email || "",
                    })
                  }
                />
              </Field>
              <Field label="Technical owner / lead dev">
                <SelectUser
                  placeholder="Unassigned"
                  value={{ _id: deal.lead_dev_id, name: deal.lead_dev_name, avatar: deal.lead_dev_avatar }}
                  onChange={(u) =>
                    onUpdate(deal._id, { lead_dev_id: u?._id || "", lead_dev_name: u?.name || "", lead_dev_avatar: u?.avatar || "", lead_dev_email: u?.email || "" })
                  }
                />
              </Field>
            </div>
          </Section>

          <Section title="Commercial">
            <Field label="Commercial arrangement">
              <InlineText
                value={deal.commercial_arrangement}
                placeholder="e.g. 30% revenue share with Oscar"
                onSave={(commercial_arrangement) => onUpdate(deal._id, { commercial_arrangement })}
                isTextArea
                rows={2}
              />
            </Field>
            <Field label="HubSpot deal">
              <InlineText value={deal.hubspot_deal_url} placeholder="https://app.hubspot.com/contacts/…" onSave={(hubspot_deal_url) => onUpdate(deal._id, { hubspot_deal_url })} />
              {deal.hubspot_deal_url && (
                <a href={deal.hubspot_deal_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-blue-600 hover:underline">
                  Open emails, calls and transcripts in HubSpot
                </a>
              )}
            </Field>
          </Section>
        </>
      )}

      {tab === "action" && (
        <>
          <Section title="Next action">
            <Field label="What happens next">
              <InlineText
                value={deal.next_action}
                placeholder="e.g. relancer la DSI sur la proposition"
                onSave={(next_action) => onUpdate(deal._id, { next_action })}
                isTextArea
                rows={2}
              />
            </Field>
            <Field label="Due date">
              <input
                type="date"
                className={`input w-full ${isOverdue(deal.next_action_at) ? "border-red-400 text-red-700" : ""}`}
                value={toInputDate(deal.next_action_at)}
                onChange={(e) => {
                  const next_action_at = e.target.value || null;
                  const payload = { next_action_at };
                  if (next_action_at && next_action_at > addDaysInput(0)) payload.sales_action_quiet_until = next_action_at;
                  onUpdate(deal._id, payload);
                }}
              />
            </Field>
            <Field label="Next-action owner">
              <SelectUser
                placeholder="Same as sales owner"
                value={{ _id: deal.next_action_owner_id, name: deal.next_action_owner_name, avatar: deal.next_action_owner_avatar }}
                onChange={(u) =>
                  onUpdate(deal._id, {
                    next_action_owner_id: u?._id || "",
                    next_action_owner_name: u?.name || "",
                    next_action_owner_avatar: u?.avatar || "",
                  })
                }
              />
            </Field>
          </Section>

          <Section title="Blocker">
            <Field label="Decision needed">
              <select
                className={`input w-full ${deal.blocker ? blockerColor(deal.blocker) : ""}`}
                value={deal.blocker || ""}
                onChange={(e) => onUpdate(deal._id, { blocker: e.target.value })}
              >
                <option value="">No blocker</option>
                {BLOCKERS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Details">
              <InlineText
                value={deal.blocker_description}
                placeholder="What exactly is blocked, and on whom?"
                onSave={(blocker_description) => onUpdate(deal._id, { blocker_description })}
                isTextArea
                rows={2}
              />
            </Field>
          </Section>

          <Section title="Close this deal">
            <p className="text-sm text-gray-500">Moves the project out of prospection, so it leaves the board. The deal keeps its history.</p>
            <div className="flex items-center gap-2">
              {EXIT_STAGES.map((exit) => (
                <button
                  key={exit.value}
                  type="button"
                  title={exit.hint}
                  onClick={() => {
                    if (!window.confirm(`Mark "${deal.name}" as ${exit.label} ?`)) return;
                    onUpdate(deal._id, { stage: exit.value });
                    onClose();
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${exit.color}`}
                >
                  {exit.label}
                </button>
              ))}
            </div>
          </Section>
        </>
      )}

      {tab === "budgets" && (
        <Section title="Budgets">
          {budgets.map((b) => (
            <div key={b._id} className="flex items-center justify-between text-sm border-b border-gray-100 py-1.5">
              <span className="text-gray-700 truncate mr-2">{b.name}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${b.signed_at ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {b.signed_at ? "Signed" : "Draft"}
                </span>
                {!b.signed_at && <span className="text-xs text-gray-500">{b.confidence || 0}%</span>}
                <span className="font-medium text-gray-900">{formatCurrency(b.amount)}</span>
              </span>
            </div>
          ))}
        </Section>
      )}

      {tab === "context" && (
        <>
          {deal.sales_scored_at && <JevRead deal={deal} />}

          <Section title="Comments">
            <Comments
              value={deal.comments || []}
              onAdd={(text) => onUpdate(deal._id, { comments: [...(deal.comments || []), { text, user_id: user._id, user_name: user.name, user_avatar: user.avatar }] })}
              onDelete={(id) => {
                if (!window.confirm("Are you sure ?")) return;
                onUpdate(deal._id, { comments: deal.comments.filter((c) => c._id !== id) });
              }}
            />
          </Section>
        </>
      )}
    </div>
  );
};

// The same fields the project edit page shows, so what is typed on the deal is what the quote and the
// invoice use later. Country stays on the edit page because it also sets the tax type.
// The buckets update_project documents for agents. Older projects carry other spellings (the project
// edit page offers "< 50k"), so a value outside the list is still shown rather than blanked.
const DEAL_SIZES = ["< 10k", "10k - 50k", "50k - 100k", "100k - 500k", "> 500k"];

const CompanyTab = ({ deal, onUpdate }) => (
  <>
    <Section title="Company">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name">
          <InlineText value={deal.clientName} placeholder="Official company name" onSave={(clientName) => onUpdate(deal._id, { clientName })} />
        </Field>
        <Field label="Industry">
          <InlineText value={deal.industry} placeholder="e.g. Healthcare, Fintech, ESG" onSave={(industry) => onUpdate(deal._id, { industry })} />
        </Field>
        <Field label="Website">
          <InlineText value={deal.website} placeholder="https://…" onSave={(website) => onUpdate(deal._id, { website })} />
          {deal.website && <ExternalLink href={deal.website} />}
        </Field>
        <Field label="LinkedIn">
          <InlineText value={deal.linkedin} placeholder="https://www.linkedin.com/company/…" onSave={(linkedin) => onUpdate(deal._id, { linkedin })} />
          {deal.linkedin && <ExternalLink href={deal.linkedin} />}
        </Field>
        <Field label="Deal size">
          <select className="input w-full" value={deal.deal_size || ""} onChange={(e) => onUpdate(deal._id, { deal_size: e.target.value })}>
            <option value="">-</option>
            {DEAL_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
            {deal.deal_size && !DEAL_SIZES.includes(deal.deal_size) && <option value={deal.deal_size}>{deal.deal_size}</option>}
          </select>
        </Field>
      </div>
    </Section>

    <Section title="Billing">
      <Field label="Registered address">
        <InlineText value={deal.clientAddress} placeholder="Street, postcode, city" onSave={(clientAddress) => onUpdate(deal._id, { clientAddress })} isTextArea rows={2} />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="SIREN / SIRET">
          <InlineText value={deal.clientSiret} onSave={(clientSiret) => onUpdate(deal._id, { clientSiret })} />
        </Field>
        <Field label="VAT number">
          <InlineText value={deal.clientVat} onSave={(clientVat) => onUpdate(deal._id, { clientVat })} />
        </Field>
      </div>
    </Section>
  </>
);

// Each edit sends the whole list back, the way comments are saved, so the API stays one PUT.
const ContactsTab = ({ deal, onUpdate }) => {
  const contacts = deal.contacts || [];
  const save = (id, field, value) => onUpdate(deal._id, { contacts: contacts.map((c) => (c._id === id ? { ...c, [field]: value } : c)) });

  return (
    <Section title="Contacts">
      {!contacts.length && <p className="text-sm text-gray-500">No contact on this deal yet.</p>}
      {contacts.map((c) => (
        <div key={c._id} className="border border-gray-200 rounded-md p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <InlineText value={c.name} placeholder="First and last name" onSave={(v) => save(c._id, "name", v)} />
            </Field>
            <Field label="Role">
              <InlineText value={c.role} placeholder="e.g. Président, décideur" onSave={(v) => save(c._id, "role", v)} />
            </Field>
            <Field label="Email">
              <InlineText value={c.email} placeholder="name@company.com" onSave={(v) => save(c._id, "email", v)} />
              {c.email && <ExternalLink href={`mailto:${c.email}`} label="Send an email" />}
            </Field>
            <Field label="Phone">
              <InlineText value={c.phone} placeholder="+33 …" onSave={(v) => save(c._id, "phone", v)} />
              {c.phone && <ExternalLink href={`tel:${c.phone.replace(/\s/g, "")}`} label="Call" />}
            </Field>
            <Field label="LinkedIn">
              <InlineText value={c.linkedin} placeholder="https://www.linkedin.com/in/…" onSave={(v) => save(c._id, "linkedin", v)} />
              {c.linkedin && <ExternalLink href={c.linkedin} />}
            </Field>
          </div>
          <Field label="Note">
            <InlineText value={c.note} placeholder="How to work with them" onSave={(v) => save(c._id, "note", v)} isTextArea rows={2} />
          </Field>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm("Are you sure ?")) return;
              onUpdate(deal._id, { contacts: contacts.filter((x) => x._id !== c._id) });
            }}
            className="text-xs text-red-600 hover:underline"
          >
            Remove contact
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onUpdate(deal._id, { contacts: [...contacts, { name: "" }] })}
        className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        Add contact
      </button>
    </Section>
  );
};

const ExternalLink = ({ href, label = "Open" }) => (
  <a href={/^[a-z]+:/i.test(href) ? href : `https://${href}`} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-blue-600 hover:underline">
    {label}
  </a>
);

// Jev returns no prose, only where it put its probability mass. Showing the whole distribution is
// the only honest explanation of a score: it makes a confident read and a coin-flip look different,
// which a single number cannot.
const JevRead = ({ deal }) => {
  const split = urgencySplit(deal);
  const confidence = Math.round((deal.sales_urgency_confidence || 0) * 100);

  return (
    <Section title="Jev read">
      {deal.sales_needs_action !== undefined && (
        <p className="text-sm text-gray-700">
          Needs someone in the next few days: <span className="font-semibold">{Math.round(deal.sales_needs_action * 100)}%</span>
        </p>
      )}

      <p className="text-sm text-gray-700">
        Urgency <span className="font-semibold">{deal.sales_urgency?.toFixed(2)}</span> out of 4, confidence {confidence}%.
        {confidence < 60 && " Jev is split — read the deal rather than the rank."}
      </p>

      <div className="space-y-1">
        {split.map((s) => (
          <div key={s.level} className="flex items-center gap-2">
            <span className="w-40 shrink-0 text-xs text-gray-600">{s.label}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded">
              <div className="h-2 bg-blue-500 rounded" style={{ width: `${Math.round(s.p * 100)}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-gray-500">{Math.round(s.p * 100)}%</span>
          </div>
        ))}
      </div>

      <div className="text-sm text-gray-700 space-y-1">
        <p>
          Still a real opportunity: <span className="font-semibold">{Math.round((deal.sales_still_live ?? 0) * 100)}%</span>
        </p>
        <p>
          Next action is concrete enough to do today: <span className="font-semibold">{Math.round((deal.sales_action_is_concrete ?? 0) * 100)}%</span>
        </p>
      </div>

      <p className="text-xs text-gray-400">
        {deal.sales_scored_model} • scored {formatDate(deal.sales_scored_at)}
      </p>
    </Section>
  );
};

// Copies a prompt that loads this deal in Claude through the accounting MCP connector. A deal is a
// project at stage prospection, so the tool that reads it is get_project.
const DealMenu = ({ deal }) => {
  const copyContext = async () => {
    const text = `Load the deal "${deal.name}" via the accounting MCP connector.\nread : get_project {id: "${deal._id}"}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Context copied");
    } catch (e) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="More">
        <HiDotsVertical className="h-4 w-4" />
      </Menu.Button>
      <Menu.Items className="absolute right-0 z-20 mt-1 w-40 rounded-md bg-white py-1 shadow-lg border border-gray-200 focus:outline-none">
        <Menu.Item>
          {({ active }) => (
            <button type="button" onClick={copyContext} className={`w-full px-3 py-1.5 text-left text-sm text-gray-700 ${active ? "bg-gray-100" : ""}`}>
              Get context
            </button>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);
